import { useState, Fragment } from 'react';
import * as authAPI from '@/api/auth';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import Input from '@/ui/inputs/Input';

const Reverify = ({ open, handleClose }) => {
  const [t] = useTranslation('signIn');
  const [isSent, setIsSent] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      handleClick();
    }
  };

  const handleError = (e, message) => {
    setError(e);
    setErrorMessage(message);
  };

  const handleClick = async () => {
    const emailRule =
      /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i;

    try {
      if (!emailRule.test(email)) return handleError(true, t('Incorrect Email address.'));

      const result = await authAPI.resendVerifyEmail(email);
      if (result && result.status === 200) {
        setIsSent(!isSent);
      }
    } catch (e) {
      if (e.response.status === 400) {
        amplitude.track('email_verification_start');
        handleError(true, t('This account is already verified.'));
      } else {
        handleError(true, t('Incorrect Email address.'));
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const closeModal = () => {
    setIsSent(false);
    setErrorMessage('');
    handleClose();
  };

  return (
    <>
      {!isSent && (
        <Transition appear show={open} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={closeModal}>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/25" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {t('Request Email Verification')}
                    </DialogTitle>
                    <div className="my-2">
                      <p className="text-sm text-gray-500">
                        {t(
                          'Have you not received the verification email? We are sorry for the inconvenience. Please enter your email address to request a new verification email.',
                        )}
                      </p>
                    </div>
                    <Input
                      className="w-full"
                      onChange={(e) => handleChange(e)}
                      onKeyDown={(e) => handleEnter(e)}
                      placeHolder={'Email'}
                    />
                    {error && <p className="text-red-400 text-sm">{errorMessage}</p>}
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className="text-white rounded-md border border-transparent bg-[#10a37f] px-4 py-2 text-sm font-medium hover:bg-[#10a37f]/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        onClick={handleClick}
                      >
                        {t('Submit')}
                      </button>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}

      {isSent && (
        <Transition appear show={open} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={closeModal}>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/25" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {t('Request Email Verification')}
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t(
                          'We sent a new verification email to your email. Please check your inbox.',
                        )}
                      </p>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className="text-white rounded-md border border-transparent bg-[#10a37f] px-4 py-2 text-sm font-medium hover:bg-[#10a37f]/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        onClick={closeModal}
                      >
                        {t('Close')}
                      </button>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
};

export default Reverify;
